import { Button, Modal, Tag } from "antd";
import { SlidersHorizontal, Filter, Check, Trash2 } from "lucide-react";
import AdvancedFilter from "./AdvancedFilter";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { useSelector } from "react-redux";

const EXCLUDED_KEYS = ["page", "size"];
const splitValues = (value = "") =>
  value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const AdvancedFilterModel = ({
  setOpenAdvancedModel,
  openAdvancedModel,
  showNumberofRecords,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const handleOk = () => {
    setOpenAdvancedModel({ open: false, key: 0 });
  };

  const handleCancel = () => {
    setOpenAdvancedModel({ open: false, key: 0 });
  };

  const onFilterChange = (key, val) => {
    if (val !== undefined) {
      searchParams.set(key, val);
    } else {
      searchParams.delete(key);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const companyFilterListRevenueRangeList = useSelector(
    (state) => state.executiveListingReducer.revenueRangeList,
  );
  const companyFilterListIndustry = useSelector(
    (state) => state.executiveListingReducer.industryList,
  );
  const companyFilterListEmployeeCountList = useSelector(
    (state) => state.executiveListingReducer.employeeCountList,
  );
  const companyFilterListCompanyType = useSelector(
    (state) => state.executiveListingReducer.companyTypeList,
  );
  const executiveFilterFunctionList = useSelector(
    (state) => state.executiveListingReducer.executiveFunctionList,
  );
  const executiveFilterLevelList = useSelector(
    (state) => state.executiveListingReducer.executiveLevelList,
  );

  const allParams = useMemo(() => {
    return Object.fromEntries(
      [...searchParams.entries()].filter(
        ([key]) => !EXCLUDED_KEYS.includes(key),
      ),
    );
  }, [searchParams]);

  const hasOtherFilters = Object.keys(allParams).length > 0;

  const formatValue = (key, value) => {
    const ids = splitValues(value);

    switch (key) {
      case "Revenue Range":
        return companyFilterListRevenueRangeList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Company Type":
        return companyFilterListCompanyType
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Employee Count":
        return companyFilterListEmployeeCountList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Industry":
        return companyFilterListIndustry
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Executive Function":
        return executiveFilterFunctionList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Executive Level":
        return executiveFilterLevelList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      default:
        return value;
    }
  };

  return (
    <Modal
      width={"65%"}
      wrapClassName="advancedfilter"
      title={
        <div className="tw-text-slate-800 tw-py-4 tw-px-6 tw-rounded-t-2xl tw-flex tw-items-center tw-gap-3.5 tw-border-b tw-border-solid tw-border-slate-100 tw-bg-slate-50 tw-font-inter">
          <SlidersHorizontal size={20} className="tw-text-[#5D44FF]" />
          <div>
            <div className="tw-text-base tw-font-bold">Filter Options</div>
            <div className="tw-text-xs tw-text-slate-500 tw-font-medium">
              Refine your executive search with powerful criteria
            </div>
          </div>
        </div>
      }
      className="delete-popup"
      open={openAdvancedModel?.open}
      onOk={handleOk}
      onCancel={handleCancel}
      maskStyle={{ background: "rgba(0,0,0,0.8)" }}
      footer={null}
    >
      {hasOtherFilters ? (
        <div className="tw-bg-slate-50/50 tw-p-5 tw-border-b tw-border-solid tw-border-slate-100 tw-font-inter">
          <div className="tw-flex tw-items-center tw-justify-between tw-pb-3">
            <div className="tw-text-sm tw-font-bold tw-text-slate-700 tw-flex tw-items-center tw-gap-2">
              <Filter size={14} className="tw-text-[#5D44FF]" /> Active Filters ({Object.entries(allParams)?.length})
            </div>
            <div
              className="tw-text-red-500 hover:tw-text-red-600 tw-text-xs tw-font-bold tw-whitespace-nowrap tw-ml-2 tw-cursor-pointer tw-transition-colors"
              onClick={() => navigate(location.pathname, { replace: true })}
            >
              Clear all
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
            {Object.entries(allParams).map(([key, value]) => (
              <Tag
                key={key}
                closable
                onClose={() => onFilterChange(key)}
                className="!tw-border-0 !tw-py-1 !tw-px-3 !tw-rounded-full !tw-bg-[#EFEAFF] !tw-text-xs !tw-font-medium !tw-text-[#5D44FF]"
              >
                {key}:{" "}
                <span className="tw-text-slate-700 tw-font-bold">{formatValue(key, value)}</span>
              </Tag>
            ))}
          </div>
        </div>
      ) : (
        <div className="tw-bg-slate-50/50 tw-px-5 tw-py-6 tw-text-slate-500 tw-text-xs tw-font-medium tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-solid tw-border-slate-100 tw-font-inter">
          <Filter size={14} className="tw-text-slate-400" /> No filters applied. Select options from the categories below.
        </div>
      )}
      <AdvancedFilter
        isModal
        openAdvancedModel={openAdvancedModel}
        showNumberofRecords={showNumberofRecords}
      />
      <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-solid tw-border-slate-100 tw-font-inter">
        <Button
          className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-sm !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
          onClick={() => navigate(location.pathname, { replace: true })}
        >
          <Trash2 size={14} className="tw-mr-1.5" /> Clear all
        </Button>
        <Button
          type="primary"
          className="!tw-h-[38px] !tw-rounded-lg !tw-bg-[#5D44FF] !tw-border-[#5D44FF] !tw-text-white !tw-px-5 tw-text-sm tw-font-bold tw-shadow-sm hover:!tw-bg-[#4B34E6] hover:!tw-border-[#4B34E6] tw-transition-all tw-duration-200 tw-flex tw-items-center"
          onClick={handleOk}
        >
          <Check size={14} className="tw-mr-1.5" /> Apply Filters
        </Button>
      </div>
    </Modal>
  );
};

export default AdvancedFilterModel;
